import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { BaseSelectOption } from 'src/shared/Forms/Selects/BaseSelect';
import { TrainerSelect } from 'src/shared/Forms/Selects/TrainerSelect';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';

import { EditEventFormValues } from './_types';

import type { Identifier } from 'src/types/utilityTypes';

export const StaffSettingsSection: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.editEventDialog.staffSettings' });
  const { canEditOthersSchedules } = usePermissionCheck();
  const { values, setFieldValue, errors, touched } =
    useFormikContext<EditEventFormValues>();

  // Only show staff settings if user can manage others
  if (!canEditOthersSchedules) {
    return null;
  }

  const handlePrimaryTrainersChange: {
    (value: Identifier | BaseSelectOption | null): void;
    (value: Identifier[] | BaseSelectOption[]): void;
  } = (value) => {
    if (Array.isArray(value)) {
      const ids = value.map((v) =>
        typeof v === 'object' ? (v as BaseSelectOption).id : v
      );
      setFieldValue('primaryTrainerIds', ids);
    }
  };

  const handleSecondaryTrainersChange: {
    (value: Identifier | BaseSelectOption | null): void;
    (value: Identifier[] | BaseSelectOption[]): void;
  } = (value) => {
    if (Array.isArray(value)) {
      const ids = value.map((v) =>
        typeof v === 'object' ? (v as BaseSelectOption).id : v
      );
      setFieldValue('secondaryTrainerIds', ids);
    }
  };

  const filterPrimaryTrainers = (
    options: BaseSelectOption[],
    { inputValue }: { inputValue: string }
  ) => {
    // Filter out trainers already selected as secondary
    return options.filter(
      (option) =>
        !values.secondaryTrainerIds.includes(option.id) &&
        option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const filterSecondaryTrainers = (
    options: BaseSelectOption[],
    { inputValue }: { inputValue: string }
  ) => {
    // Filter out trainers already selected as primary
    return options.filter(
      (option) =>
        !values.primaryTrainerIds.includes(option.id) &&
        option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  return (
    <Box
      component="fieldset"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        m: 0,
      }}
    >
      <Typography variant="h6" component="legend">
        {t('title')}
      </Typography>

      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TrainerSelect
              name="primaryTrainerIds"
              label={t('primaryTrainers')}
              multiple
              value={values.primaryTrainerIds}
              onChange={handlePrimaryTrainersChange}
              filterOptions={filterPrimaryTrainers}
              disabled={values.noTrainers}
              error={
                !values.noTrainers &&
                touched.primaryTrainerIds &&
                !!errors.primaryTrainerIds
              }
              helperText={
                !values.noTrainers &&
                touched.primaryTrainerIds &&
                errors.primaryTrainerIds
                  ? String(errors.primaryTrainerIds)
                  : undefined
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TrainerSelect
              name="secondaryTrainerIds"
              label={t('secondaryTrainers')}
              multiple
              value={values.secondaryTrainerIds}
              onChange={handleSecondaryTrainersChange}
              filterOptions={filterSecondaryTrainers}
              disabled={values.noTrainers}
            />
          </Grid>
        </Grid>

        <Box>
          <Checkbox
            useSwitch
            label={t('disableTrainers')}
            checked={values.noTrainers}
            onChange={(e) => {
              setFieldValue('noTrainers', e.target.checked);
              if (e.target.checked) {
                setFieldValue('primaryTrainerIds', []);
                setFieldValue('secondaryTrainerIds', []);
              }
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};
